import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheet.lifestyle';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-4 text-foreground-500 font-sans">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-sm font-semibold bg-accent-500 text-white rounded-full hover:bg-accent-600 transition-all whitespace-nowrap">
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <SEO
        title={`${article.title} | Mulebuy Spreadsheet Hub`}
        description={article.description}
        keywords={`mulebuy spreadsheet, ${article.category.toLowerCase()}, ${article.title.toLowerCase()}, mulebuy spreadsheet guide`}
        canonical={`${siteUrl}/article/${article.slug}`}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.description,
          author: {
            '@type': 'Organization',
            name: 'Mulebuy Spreadsheet Editorial Team',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Mulebuy Spreadsheet Hub',
            url: siteUrl,
          },
          datePublished: '2026-06-22',
          dateModified: '2026-06-22',
          url: `${siteUrl}/article/${article.slug}`,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/article/${article.slug}`,
          },
        }}
      />
      <Navbar />

      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-8 lg:px-12">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs font-sans text-foreground-400">
              <li>
                <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-foreground-400">{article.category}</span>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-accent-600 truncate max-w-[200px] inline-block">{article.title}</span>
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="inline-block px-3 py-1 text-xs font-semibold text-accent-700 bg-accent-50 rounded-full mb-4 font-sans whitespace-nowrap">
              {article.category}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-500 font-sans max-w-2xl leading-relaxed">
              {article.description}
            </p>
            <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400 font-sans">
              <span>By Mulebuy Spreadsheet Editorial Team</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="space-y-10 font-sans text-foreground-700 leading-relaxed">
            {article.sections.map((section, index) => (
              <section key={index}>
                {section.image && (
                  <div className="mb-6 rounded-2xl overflow-hidden">
                    <img
                      src={section.image}
                      alt={section.heading}
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                )}
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content.split('\n\n').map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-sm md:text-base mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <section className="bg-accent-50/60 rounded-2xl p-6 md:p-8 border border-accent-100/60">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Continue Your Mulebuy Spreadsheet Journey
              </h2>
              <p className="text-sm text-foreground-600 mb-5">
                Ready to explore more Mulebuy spreadsheet resources? Browse our complete library of guides, comparisons, and expert insights to deepen your sourcing knowledge.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-accent-500 text-white rounded-full hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Mulebuy Spreadsheet Hub
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Visit Main Store
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                More Mulebuy Spreadsheet Articles
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="group flex items-start gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-accent-200 transition-all cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 flex-shrink-0 group-hover:bg-accent-100 transition-colors">
                        <i className="ri-article-line text-sm"></i>
                      </div>
                      <div>
                        <h3 className="font-sans text-xs font-semibold text-foreground-950 group-hover:text-accent-700 transition-colors line-clamp-2">
                          {a.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-400">{a.readTime}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}